import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, of } from 'rxjs';
import { mergeMap, catchError, finalize } from 'rxjs/operators';
import { IngestionModalComponent } from './ingestion-modal/ingestion-modal';
import { DocumentTableComponent } from '../../../../shared/document-table-component/document-table-component';
import { Document } from '../../../../shared/document-directive';
import { IngestionDto, UploadItem, DocumentItem } from '../../../../shared/interfaces';
import { ToastService } from '../../../../services/toast-service';
import { DuplicatesActionModalComponent } from './duplicates-action-modal/duplicates-action-modal.component';
import { TaskService } from '../../../../services/task-service';

@Component({
  selector: 'app-to-process',
  standalone: true,
  imports: [CommonModule, DocumentTableComponent],
  templateUrl: './to-process.html',
  styleUrl: './to-process.scss',
})
export class ToProcess extends Document {
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);
  private readonly taskService = inject(TaskService)

  status: string = 'input';
  task: string = 'trigger-scan';
  currentPath = signal<string>('input/');
  selectedItems = signal<DocumentItem[]>([]);
  sourceType: string = 'input'

  // Limiti per l'upload
  readonly MAX_FILES = 1500;
  readonly MAX_CONCURRENT_UPLOADS = 5;

  rootFolderName = computed(() => {
    const segments = this.currentPath().split('/').filter((s: string) => s.length > 0);
    return segments.length >= 2 ? segments[1] : null;
  });

  isInFolder = computed(() => this.currentPath().split('/').filter((s: string) => s.length > 0).length >= 2);

  canRunIngestion = computed(() => {
    const path = this.currentPath();
    const segments = path.split('/').filter((s: string) => s.length > 0);
  
    if (segments.length < 2) return false;
    
    const hasDocuments = this.documents().some((doc: DocumentItem) => doc.percorso.startsWith(path) && doc.percorso !== path);
    const hasSelection = this.selectedItems().length > 0;

    return hasDocuments && hasSelection;
  });

  isProcessing = this.taskService.isProcessing;
  private readonly globalTaskState = this.taskService.taskState;

  isUploadMenuOpen = signal(false);
  isUploading = signal(false);
  isDragging = signal(false);
  uploadProgress = signal({ completed: 0, total: 0 });
  private dragCounter = 0;

  constructor() {
    super();
    effect(() => {
      if (this.globalTaskState()?.status === 'COMPLETED') this.loadData();
    });
  }

  onSelectionChanged(items: DocumentItem[]) {
    this.selectedItems.set(items);
  }

  openIngestionModal() {
    const index = this.rootFolderName();
    if (!index) return;
    const modalRef = this.modalService.open(IngestionModalComponent, { centered: true, windowClass: 'agentic-custom-modal' });
    modalRef.componentInstance.indexName = index;
    modalRef.result.then(res => res && this.confirmIngestion(res)).catch(() => {});
  }

  confirmIngestion(indexName: string) {
    // 1. Estraiamo la lista dei nomi dei file
    const selectedDocs = this.selectedItems().map(doc => doc.percorso);

    // 2. Prepariamo il payload iniziale (senza action)
    const payload: IngestionDto = { 
      indexName: indexName,
      docs: selectedDocs,
      sourceType: 'input'
    };

    // 3. Lanciamo l'esecuzione
    this.executeIngestion(payload);
  }

  private executeIngestion(payload: IngestionDto) {
    this.isLoading.set(true);

    this.documentService.ingestDocuments(this.task, payload).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        
        // GESTIONE DUPLICATI
        if (res.status === 'DUPLICATES_FOUND' && res.conflicts) {
          this.handleDuplicatesModal(payload, res);
        } 
        // GESTIONE REGOLARE (Iniziato)
        else if (res.status === 'STARTED' && res.taskId) {
          this.taskService.registerNewTask(res.taskId);
          this.selectedItems.set([]); 
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  private handleDuplicatesModal(originalPayload: IngestionDto, res: any) {
    const modalRef = this.modalService.open(DuplicatesActionModalComponent, { 
      centered: true, 
      windowClass: 'agentic-custom-modal' 
    });

    // PASSIAMO L'INTERO ARRAY DI OGGETTI!
    modalRef.componentInstance.duplicatesList = res.conflicts;
    modalRef.componentInstance.conflictType = res.conflictType; 
    
    const totalDocsSent = originalPayload.docs?.length || 0;
    modalRef.componentInstance.allDuplicates = (totalDocsSent === res.conflicts.length);

    modalRef.result.then((userChoice: 'replace' | 'keep' | 'ignore' | 'cancel') => {
      if (!userChoice || userChoice === 'cancel') return; 

      if (userChoice === 'replace') {
        this.executeIngestion({ ...originalPayload, action: 'replace' });
      } else if (userChoice === 'keep') {
        this.executeIngestion({ ...originalPayload, action: 'keep' });
      } else if (userChoice === 'ignore') {
        
        const duplicateIncomingPaths = res.conflicts.map((c: any) => c.incomingFile);
        const filteredDocs = originalPayload.docs?.filter(doc => !duplicateIncomingPaths.includes(doc)) || [];
        
        if (filteredDocs.length === 0) {
          this.toastService.show('Tutti i file selezionati erano duplicati. Operazione ignorata.', 'info');
          this.selectedItems.set([]); 
          return;
        }
        
        this.executeIngestion({ ...originalPayload, docs: filteredDocs });
      }
    }).catch(() => {});
  }

  // --- Funzioni Upload UI ---
  toggleUploadMenu() {
    if (!this.isInFolder()) return;
    this.isUploadMenuOpen.update(v => !v);
  }

  triggerFileInput(inputElement: HTMLInputElement) {
    this.isUploadMenuOpen.set(false);
    inputElement.click();
  }

  // --- Drag & Drop ---
  onDragEnter(e: DragEvent) { 
    if (!this.isInFolder()) {
      return; 
    }
    e.preventDefault(); 
    this.dragCounter++; 
    this.isDragging.set(true); 
  }

  onDragOver(e: DragEvent) { 
    if (this.isInFolder()) {
      e.preventDefault(); 
    }
  }

  onDragLeave(e: DragEvent) { 
    if (!this.isInFolder()) {
      return; 
    }
    this.dragCounter--; 
    if (this.dragCounter === 0) {
      this.isDragging.set(false); 
    }
  }

  async onDrop(e: DragEvent) {
    if (!this.isInFolder()) return;
    e.preventDefault(); this.dragCounter = 0; this.isDragging.set(false);
    if (!e.dataTransfer?.items) return;
    this.isUploading.set(true);
    try {
      const uploadItems: UploadItem[] = [];
      const base = this.currentPath().replace(/^input\//, '');
      const promises = Array.from(e.dataTransfer.items)
        .filter((i: any) => i.kind === 'file')
        .map((i: any) => this.traverseFileTree(i.webkitGetAsEntry(), base, uploadItems));
      await Promise.all(promises);
      this.startUploadProcess(uploadItems);
    } catch { this.isUploading.set(false); }
  }

  private traverseFileTree(item: any, path: string, result: UploadItem[]): Promise<void> {
    return new Promise((resolve) => {
      if (item.isFile) {
        item.file((f: File) => {
          if (!f.name.startsWith('.')) result.push({ file: f, path: path + f.name });
          resolve();
        });
      } else if (item.isDirectory) {
        const reader = item.createReader();
        reader.readEntries(async (entries: any[]) => {
          await Promise.all(entries.map((e: any) => this.traverseFileTree(e, path + item.name + '/', result)));
          resolve();
        });
      }
    });
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const base = this.currentPath().replace(/^input\//, '');
    const items = Array.from(input.files).filter((f: File) => !f.name.startsWith('.')).map((f: File) => ({
      file: f, path: base + (f.webkitRelativePath || f.name)
    }));
    this.startUploadProcess(items);
  }

  private startUploadProcess(items: UploadItem[]) {
    if (!items.length) { this.isUploading.set(false); return; }
    
    // Controllo limite file
    if (items.length > this.MAX_FILES) {
      this.toastService.show(`Puoi caricare massimo ${this.MAX_FILES} file alla volta.`, 'error');
      this.isUploading.set(false);
      return;
    }

    this.isUploading.set(true);
    this.uploadProgress.set({ completed: 0, total: items.length });
    
    from(items).pipe(
      mergeMap((i: UploadItem) => this.documentService.uploadSingleFile(i.file, i.path).pipe(catchError(() => of(null))), this.MAX_CONCURRENT_UPLOADS),
      finalize(() => { this.isUploading.set(false); this.loadData(); })
    ).subscribe(() => this.uploadProgress.update(p => ({ ...p, completed: p.completed + 1 })));
  }
}